/**
 * HORO-10426: shared "submit from the homepage → open the chat" flow.
 *
 * Mirrors the proven ChatBar landing-submit path (common/chat/ChatBar):
 * create the chat with `skipHello=1` (so the advisor's canned greeting
 * doesn't preempt the user's question), dispatch the first message
 * immediately, then navigate to the chat. The pending-send bubble survives
 * the route transition, so the question shows as the user's first message
 * and the advisor's reply streams in — no reliance on any auto-send effect.
 *
 * When there's no message (an advisor picked from the carousel with no
 * question), route to the advisor landing instead; the chat is created on
 * first engagement there, so the advisor greets the user normally.
 *
 * On failure we still route to the advisor landing so a transient
 * create-chat error never strands the user on the homepage.
 */
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { APP_URL_CHAT } from '../../../const';
import { useAdvisorLockGate } from '../../../hooks/useAdvisorLockGate';
import { useMessageLimitGate } from '../../../hooks/useMessageLimitGate';
import { getUser, setUserMessagesLimits, setUserZaraTrial } from '../../../store/slices/authSlice';
import { sendNewChatMessage, startChatWithAdvisor } from '../../../store/slices/chatsSlice';
import { gaEvents } from '../../../utils/analytics';
import { updateMessagesLimitCounter } from '../../../utils/helper';
import { pushRecentAdvisorAlias } from '../../common/Menu/recentAdvisorsStorage';

export const useStartHomeChat = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(getUser);
    const isUser = !!user?.id;
    const checkMessageLimitBlocked = useMessageLimitGate();
    const checkAdvisorLockBlocked = useAdvisorLockGate();
    const [submitting, setSubmitting] = useState(false);

    const startHomeChat = useCallback(
        async ({ content = '', advisorAlias = 'veda', source = 'input' } = {}) => {
            const message = content.trim();
            if (submitting) return;

            // HORO-10504: advisor-access gate (anonymous character-access AND
            // Zara's premium-subscriber gate — this used to check
            // `canStartChatWithAdvisor` directly, which only covers the
            // anonymous case) — checked before the message-quota gate below,
            // since it's the more specific reason ("you can't chat with this
            // advisor at all yet" beats "you're out of messages" when both
            // happen to be true).
            if (checkAdvisorLockBlocked(advisorAlias)) return;

            // HORO-10455: when there's a question to send, gate on the message
            // quota first. If the user is out of messages the gate opens the
            // paywall (auth) or sign-in / anon modal at submission and we abort
            // — otherwise the send would be rejected and the user would land in
            // a blank chat with no feedback. Advisor-only selections (no
            // message) create no send, so they skip the gate.
            if (message && checkMessageLimitBlocked()) return;

            setSubmitting(true);

            // Anonymous recents mirror the sidebar behaviour so a returning
            // anon user sees the advisor they just chatted with up top.
            if (!isUser) pushRecentAdvisorAlias(advisorAlias);
            gaEvents.homeChatStart(source, advisorAlias, !!message);

            // No question — just an advisor selection. Land on the advisor
            // page; the chat is created (with greeting) on first engagement.
            if (!message) {
                navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
                setSubmitting(false);
                return;
            }

            try {
                // `skipHello=1` so the user's question is the first message,
                // not a canned advisor greeting.
                const result = await dispatch(startChatWithAdvisor(`alias/${advisorAlias}?skipHello=1`)).unwrap();
                const chatId = result?.id;
                const alias = result?.advisor?.alias || advisorAlias;
                if (chatId) {
                    // HORO-10455: count this first message only AFTER the send
                    // resolves successfully — the user gets the advisor's response
                    // before the counter moves, and a failed/unanswered send never
                    // charges them. The homepage/landing send otherwise bypasses the
                    // in-chat sendWithLimitTracking counting, leaving the first
                    // message of every homepage-started chat uncounted (a free
                    // message for anon, which also under-counted the paywall gate).
                    // Anon: bump the client-side counter; logged-in: sync the
                    // server-authoritative limits from the response.
                    dispatch(sendNewChatMessage({ chatId, content: message, attachments: [], collapseParagraph: true })).then((res) => {
                        if (!sendNewChatMessage.fulfilled.match(res)) return;
                        if (isUser) {
                            dispatch(setUserMessagesLimits(res.payload.limits));
                            // HORO-10599: this path is also how a registered-free
                            // account's very first Zara message gets sent (the
                            // welcome modal's chip taps route through here) —
                            // sync the trial counter the same way ChatBar/
                            // sendWithLimitTracking do, so it doesn't read stale
                            // (0 used) once the chat lands.
                            if (res.payload.zaraTrial) {
                                dispatch(setUserZaraTrial(res.payload.zaraTrial));
                            }
                        } else {
                            updateMessagesLimitCounter(chatId);
                        }
                    });
                    navigate(`/${APP_URL_CHAT}/${alias}/${chatId}`);
                } else {
                    navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
                }
            } catch (error) {
                navigate(`/${APP_URL_CHAT}/${advisorAlias}`);
            } finally {
                setSubmitting(false);
            }
        },
        [dispatch, navigate, isUser, submitting, checkMessageLimitBlocked, checkAdvisorLockBlocked]
    );

    return { startHomeChat, submitting };
};
